import lineLength from "../external/cc-lib/dist/functions/quill/lineLength.js";

export const EXCESS_CHARS_CLASS = "cc-excess-chars";
export const EXCESS_HIGHLIGHT_NAME = "cc-excess-chars";

/** @type {Map<Element, Range[]>} */
const excessHighlightRoots = new Map();

/**
 * @param {unknown} limit
 * @returns {limit is number}
 */
function isFiniteLimit(limit) {
    return typeof limit === "number" && Number.isFinite(limit);
}

/**
 * @param {ParentNode} element
 * @param {Document} doc
 * @returns {Text[]}
 */
function collectTextNodes(element, doc) {
    const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
    const nodes = [];
    while (walker.nextNode()) {
        const node = /** @type {Text} */ (walker.currentNode);
        // Ruby annotations are not counted toward the line length
        if (!node.parentElement?.closest("rt")) {
            nodes.push(node);
        }
    }
    return nodes;
}

/**
 * Find where a line first exceeds `limit`, counting characters with the same
 * rule as the error checks and metrics (collapsed/edge spaces don't count).
 *
 * @param {Element} paragraph
 * @param {number} limit
 * @param {Document} doc
 * @returns {{ nodes: Text[], nodeIndex: number, offset: number } | null}
 */
function findOverflowStart(paragraph, limit, doc) {
    const nodes = collectTextNodes(paragraph, doc);
    const mask = lineLength.getCountedMask(nodes.map((node) => node.data).join(""));

    let counted = 0;
    let overflowAt = -1;
    for (let i = 0; i < mask.length; i++) {
        if (mask[i] && ++counted > limit) {
            overflowAt = i;
            break;
        }
    }

    if (overflowAt === -1) {
        return null;
    }

    let seen = 0;
    for (let nodeIndex = 0; nodeIndex < nodes.length; nodeIndex++) {
        const len = nodes[nodeIndex].data.length;
        if (overflowAt < seen + len) {
            return { nodes, nodeIndex, offset: overflowAt - seen };
        }
        seen += len;
    }

    return null;
}

/**
 * @param {ParentNode} root
 * @returns {Element[]}
 */
function getParagraphs(root) {
    if (root instanceof Element && root.tagName === "P") {
        return [root];
    }
    return Array.from(root.querySelectorAll("p"));
}

/**
 * Wrap overflowing text in a paragraph, preserving existing markup.
 * @param {Element} paragraph
 * @param {number} limit
 * @param {Document} doc
 */
function wrapExcessInParagraph(paragraph, limit, doc) {
    const start = findOverflowStart(paragraph, limit, doc);
    if (!start) {
        return;
    }

    const { nodes, nodeIndex, offset } = start;

    for (let i = nodeIndex; i < nodes.length; i++) {
        const node = nodes[i];
        const len = node.data.length;
        if (len === 0) {
            continue;
        }

        const splitAt = i === nodeIndex ? offset : 0;

        let excess = node;
        if (splitAt > 0 && splitAt < len) {
            excess = node.splitText(splitAt);
        }

        if (!excess.data.length) {
            continue;
        }

        if (excess.parentElement?.classList.contains(EXCESS_CHARS_CLASS)) {
            continue;
        }

        const span = doc.createElement("span");
        span.className = EXCESS_CHARS_CLASS;
        excess.parentNode?.insertBefore(span, excess);
        span.appendChild(excess);
    }
}

/**
 * Highlight characters past `limit` on each line without stripping inline
 * formatting. Uses DOM APIs so literal < and > stay text, not tags.
 *
 * @param {string} html
 * @param {unknown} limit
 * @returns {string}
 */
export function highlightExcessCharacters(html, limit) {
    if (!html) {
        return "";
    }
    if (!isFiniteLimit(limit)) {
        return html;
    }

    const doc = new DOMParser().parseFromString(html, "text/html");
    getParagraphs(doc.body).forEach((p) => {
        wrapExcessInParagraph(p, limit, doc);
    });
    return doc.body.innerHTML;
}

/**
 * Collect live Ranges for overflowing characters. Does not mutate the DOM,
 * so it is safe to use on Quill's editor root.
 *
 * @param {ParentNode | null | undefined} root
 * @param {unknown} limit
 * @returns {Range[]}
 */
export function collectExcessRanges(root, limit) {
    if (!root || !isFiniteLimit(limit) || !root.ownerDocument) {
        return [];
    }

    const doc = root.ownerDocument;
    const ranges = [];

    for (const paragraph of getParagraphs(root)) {
        const start = findOverflowStart(paragraph, limit, doc);
        if (!start) {
            continue;
        }

        const { nodes, nodeIndex, offset } = start;

        for (let i = nodeIndex; i < nodes.length; i++) {
            const node = nodes[i];
            const len = node.data.length;
            const splitAt = i === nodeIndex ? offset : 0;

            if (splitAt >= len) {
                continue;
            }

            const range = doc.createRange();
            range.setStart(node, splitAt);
            range.setEnd(node, len);
            ranges.push(range);
        }
    }

    return ranges;
}

function rebuildHighlight() {
    if (typeof CSS === "undefined" || !CSS.highlights || typeof Highlight === "undefined") {
        return;
    }

    const highlight = new Highlight();
    for (const ranges of excessHighlightRoots.values()) {
        for (const range of ranges) {
            if (range.startContainer.isConnected) {
                highlight.add(range);
            }
        }
    }
    CSS.highlights.set(EXCESS_HIGHLIGHT_NAME, highlight);
}

/**
 * Apply a visual-only overflow highlight to a live DOM root (e.g. Quill).
 * No-ops when the CSS Custom Highlight API is unavailable.
 *
 * @param {Element | null | undefined} root
 * @param {unknown} limit
 */
export function applyExcessCharacterHighlight(root, limit) {
    if (!root || typeof CSS === "undefined" || !CSS.highlights) {
        return;
    }

    excessHighlightRoots.set(root, collectExcessRanges(root, limit));
    rebuildHighlight();
}

/**
 * @param {Element | null | undefined} root
 */
export function clearExcessCharacterHighlight(root) {
    if (!root) {
        return;
    }

    excessHighlightRoots.delete(root);
    rebuildHighlight();
}
